import React, { useState, useEffect, useRef } from 'react';
import { Nav } from '../../components/nav';
import { useAuth } from '../../navigation/AuthProvider';
import {
  add_new_project_to_company,
  delete_project,
  edit_project,
  remove_employee_from_project,
  add_employee_to_project
} from '../../utils/ProjectRequests';
import '../css/Project.css';
import axios from 'axios';
import * as XLSX from "xlsx";
import { BACKEND_URL } from '../../constants';

import { getEmployeesAssignedToShiftsButNotInProject } from "../../utils/getEmployeesAssignedToShiftsButNotInProject";

import { Toast } from '../../components/ToastPopup';
import {
  FaCalendarAlt,
  FaPlus,
  FaEllipsisH
} from 'react-icons/fa';

import { SiGooglesheets } from 'react-icons/si';

import { getPermission } from '../../permissions/rolePermissions';

import ImportProjectsPopup from '../../components/imports/ImportProjectsPopup';

export const Projects = () => {
  const {
    company,
    projects,
    setProjects,
    user,
    customers,
    setCustomers,
    employees,
    // setLoading,
    setDraftSchedule,
    draftSchedule
  } = useAuth();

  const employeesPermission = getPermission(user.role, "projects");
  const canEditProjects = employeesPermission === "edit"

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const [allProjects, setAllProjects] = useState(projects || []);
  const [allCustomers, setAllCustomers] = useState(customers || []);
  const [allEmployees, setAllEmployees] = useState(employees || []);
  const [allShifts, setAllShifts] = useState(draftSchedule || []);

  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectLocation, setNewProjectLocation] = useState('');
  const [newProjectInstructions, setNewProjectInstructions] = useState('');
  const [newProjectCustomer, setNewProjectCustomer] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const [projectPopupVisible, setProjectPopupVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const [employeeForProjectInput, setEmployeeForProjectInput] = useState('');
  const [selectedEmployeeForProject, setSelectedEmployeeForProject] = useState(null);

  const [activeMenu, setActiveMenu] = useState(null);

  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showImportProjectsPopup, setShowImportProjectsPopup] = useState(false);

  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const menuRef = useRef(null);

  useEffect(() => {
    setFilteredProjects(
      allProjects.filter((project) =>
        project.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, allProjects]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const monthNames = ["Januari", "Februari", "Mars", "April", "Maj", "Juni", "Juli", "Augusti", "September", "Oktober", "November", "December"];
  const now = new Date();
  const initYear = now.getFullYear();
  const initMonth = now.getMonth() + 1;

  const [inputYear, setInputYear] = useState(initYear);
  const [inputMonth, setInputMonth] = useState(initMonth);

  const [selectedYear, setSelectedYear] = useState(initYear);
  const [selectedMonth, setSelectedMonth] = useState(initMonth);

  const monthLabel = `${monthNames[(selectedMonth - 1 + 12) % 12]} ${selectedYear}`;

  const pad2 = (n) => String(n).padStart(2, '0');
  const cacheRef = useRef(new Map());
  const inFlightRef = useRef(new Set());
  const lastReqIdRef = useRef(0);
  const didInitRef = useRef(false);

  const makeKey = (uid, cid, y, m) => `${uid}:${cid}:${y}-${pad2(m)}`;

  const setFromCacheOrEmpty = (key) => {
    if (cacheRef.current.has(key)) {
      setAllShifts(cacheRef.current.get(key));
    } else {
      setAllShifts([]);
    }
  };

  const fetchShiftsFor = async (year, month) => {
    if (!user?._id || !company?._id) return;
    const key = makeKey(user._id, company._id, year, month);

    setFromCacheOrEmpty(key);

    if (cacheRef.current.has(key) || inFlightRef.current.has(key)) {
      if (cacheRef.current.has(key)) {
        setSelectedYear(year);
        setSelectedMonth(month);
      }
      return;
    }

    inFlightRef.current.add(key);
    const reqId = ++lastReqIdRef.current;

    try {
      setIsFetching(true);
      const resp = await axios.get(`${BACKEND_URL}/get-shifts-for-month`, {
        params: {
          userId: user._id,
          companyId: company._id,
          selectedMonth: Number(month),
          selectedYear: Number(year)
        }
      });

      if (reqId < lastReqIdRef.current) return;

      if (resp?.data?.status === "success") {
        const shifts = Array.isArray(resp.data.shifts) ? resp.data.shifts : [];
        cacheRef.current.set(key, shifts);
        setAllShifts(shifts);
        setSelectedYear(year);
        setSelectedMonth(month);
      } else {
        cacheRef.current.set(key, []);
        setAllShifts([]);
        setToastType("error");
        setToastMessage(resp?.data?.errorText || "Kunde inte hämta skift.");
      }
    } catch (err) {
      if (reqId < lastReqIdRef.current) return;
      console.error("Fetch month error:", err);
      cacheRef.current.set(key, []);
      setAllShifts([]);
      setToastType("error");
      setToastMessage("Ett fel inträffade vid hämtning av skift.");
    } finally {
      inFlightRef.current.delete(key);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (didInitRef.current) return;
    if (!user?._id || !company?._id) return;

    didInitRef.current = true;
    fetchShiftsFor(selectedYear, selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, company?._id]);

  const onChangeYear = (e) => {
    const y = Number(e.target.value);
    setInputYear(y);
    fetchShiftsFor(y, inputMonth);
  };

  const onChangeMonth = (e) => {
    const m = Number(e.target.value);
    setInputMonth(m);
    fetchShiftsFor(inputYear, m);
  };

  const [unassignedWarnings, setUnassignedWarnings] = useState([]);
  const updateUnassignedWarnings = (customProjects = allProjects) => {
    if (!allShifts || allShifts.length === 0) {
      setUnassignedWarnings([]);
      return;
    }

    const currentYear = Number(selectedYear);
    const currentMonth = Number(selectedMonth);

    const warnings = getEmployeesAssignedToShiftsButNotInProject({
      allProjects: customProjects,
      allEmployees,
      shifts: allShifts,
      currentMonth,
      currentYear
    });

    setUnassignedWarnings(warnings);
  };

  useEffect(() => {
    updateUnassignedWarnings();
  }, [allShifts, allProjects, allEmployees, selectedYear, selectedMonth]);

  const handleQuickAddToProject = async (employeeId, projectId) => {
    const data = await add_employee_to_project({
      user,
      company,
      employeeId,
      project: { _id: projectId }
    });

    if (data.status === "success") {
      setProjects(prev =>
        prev.map(p => p._id === projectId ? data.project : p)
      );
      setAllProjects(prev => {
        const updated = prev.map(p => p._id === projectId ? data.project : p);
        updateUnassignedWarnings(updated);
        return updated;
      });
      setToastType("success");
      setToastMessage("Användare tillagd i projektet!");
    } else {
      setToastType("error");
      setToastMessage(data.errorText || "Kunde inte lägga till användaren.");
    }
  };

  const handleQuickAddAllEmployeesToProjects = async () => {
    if (!unassignedWarnings || unassignedWarnings.length === 0) {
      setToastType("info");
      setToastMessage("Inga konflikter att åtgärda.");
      return;
    }

    const confirmed = window.confirm(
      "Är du säker på att du vill lägga till alla berörda anställda i de projekt där de redan är schemalagda?"
    );
    if (!confirmed) return;

    try {
      setIsBulkAdding(true);

      const pairSet = new Set();
      const pairs = [];
      for (const w of unassignedWarnings) {
        if (!w?.projectId || !w?.employeeId) continue;
        const key = `${w.projectId}_${w.employeeId}`;
        if (!pairSet.has(key)) {
          pairSet.add(key);
          pairs.push({ projectId: w.projectId, employeeId: w.employeeId });
        }
      }

      if (pairs.length === 0) {
        setToastType("info");
        setToastMessage("Inga giltiga par att lägga till.");
        return;
      }

      const resp = await axios.post(`${BACKEND_URL}/add-many-employees-to-many-projects`, {
        userId: user._id,
        companyId: company._id,
        pairs
      });

      const data = resp?.data;
      if (data?.status === "success") {
        const updatedList = data.projects || data.projectsAllSorted || null;

        if (updatedList && Array.isArray(updatedList)) {
          setProjects(updatedList);
          setAllProjects(updatedList);
          updateUnassignedWarnings(updatedList);
        } else if (data.affectedProjectIds?.length && Array.isArray(data.projects)) {
          const affected = new Set(data.affectedProjectIds);
          const merged = allProjects.map(p => {
            const repl = data.projects.find(x => x._id === p._id);
            return affected.has(p._id) && repl ? repl : p;
          });
          setProjects(merged);
          setAllProjects(merged);
          updateUnassignedWarnings(merged);
        } else {
          updateUnassignedWarnings();
        }

        setToastType("success");
        setToastMessage("Alla konflikter åtgärdade – anställda tillagda i sina projekt.");
      } else {
        setToastType("error");
        setToastMessage(data?.errorText || "Kunde inte lägga till anställda i projekten.");
      }
    } catch (err) {
      console.error("Bulk add error:", err);
      setToastType("error");
      setToastMessage("Ett fel uppstod vid massläggning.");
    } finally {
      setIsBulkAdding(false);
    }
  };

  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
    clearForm();
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setNewProjectName(project.name);
    setNewProjectLocation(project.location);
    setNewProjectInstructions(project.instructions);

    const associatedCustomer = allCustomers.find(cust =>
      (project.customer && project.customer._id)
        ? cust._id === project.customer._id
        : cust._id === project.customer
    );
    setNewProjectCustomer(associatedCustomer || null);

    setIsPopupOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const newProject = {
      name: newProjectName,
      location: newProjectLocation,
      instructions: newProjectInstructions,
      customerId: newProjectCustomer ? newProjectCustomer._id : null,
    };

    const data = selectedProject
      ? await edit_project({ user, company, project: selectedProject, updatedProjectValues: newProject })
      : await add_new_project_to_company({ newProject, user, company });

    if (data && data.status === 'success') {
      setProjects(data.projects);
      setAllProjects(data.projects);
      clearForm();
      setToastType("success");
      setToastMessage(selectedProject ? "Projekt redigerat!" : "Projekt tillagt!");
      setIsPopupOpen(false);
    } else {
      setToastType("error");
      setToastMessage(data.errorText);
    }
  };

  const handleDelete = async (project) => {
    if (window.confirm('Är du säker på att du vill radera projektet?   Alla existerande pass som använder projektet kommer försvinna')) {
      try {
        const data = await delete_project({ project, company, user });
        if (data && data.status === 'success') {
          setProjects(data.projects);
          setAllProjects(data.projects);
          setDraftSchedule(data.updatedShifts);
          setToastType("success");
          setToastMessage("Projekt raderat!");
        } else {
          setToastType("error");
          setToastMessage(data.errorText);
        }
      } catch (e) {
        console.error(e);
        setToastType("error");
        setToastMessage("Kunde inte radera projektet.");
      }
    }
  };

  const clearForm = () => {
    setNewProjectName('');
    setNewProjectInstructions('');
    setNewProjectLocation('');
    setNewProjectCustomer(null);
    setSelectedProject(null);
  };

  const toggleMenu = (projectId, event) => {
    event.stopPropagation();
    setActiveMenu(activeMenu === projectId ? null : projectId);
  };

  const handleShowProjectPopupClicked = (project) => {
    setSelectedProject(project);
    setProjectPopupVisible(true);
  };

  // ===== Visibility helpers =====
  const canSeeAll = (project, empId) => {
    const map = project?.memberVisibility || {};
    const key = String(empId || "");
    return typeof map[key] === "boolean" ? map[key] : true; // default true
  };

  const [visBusy, setVisBusy] = useState(false);

  // Optimistic UI toggle:
  const setMemberVisibility = async ({ projectId, targetUserId, canSeeAllShifts }) => {
    if (!selectedProject?._id) return;

    // 1) Optimistiskt uppdatera UI direkt
    setSelectedProject(prev => {
      if (!prev) return prev;
      const map = { ...(prev.memberVisibility || {}) };
      map[String(targetUserId)] = !!canSeeAllShifts;
      return { ...prev, memberVisibility: map };
    });

    setAllProjects(prev => prev.map(p => {
      if (p._id !== projectId) return p;
      const map = { ...(p.memberVisibility || {}) };
      map[String(targetUserId)] = !!canSeeAllShifts;
      return { ...p, memberVisibility: map };
    }));

    try {
      setVisBusy(true);
      const resp = await axios.post(`${BACKEND_URL}/projects/set-member-visibility`, {
        userId: user._id,
        companyId: company._id,
        projectId,
        targetUserId,
        canSeeAllShifts: !!canSeeAllShifts
      });
      const data = resp?.data;

      if (data?.status === "success" && data?.project) {
        // 2) Reconcile med serverns svar
        setProjects(prev => prev.map(p => p._id === data.project._id ? data.project : p));
        setAllProjects(prev => prev.map(p => p._id === data.project._id ? data.project : p));
        if (selectedProject && selectedProject._id === data.project._id) {
          setSelectedProject(data.project);
        }
        setToastType("success");
        setToastMessage("Synligheten uppdaterades.");
      } else {
        setToastType("error");
        setToastMessage(data?.errorText || "Kunde inte uppdatera synlighet.");
      }
    } catch (err) {
      console.error("set-member-visibility error:", err);
      setToastType("error");
      setToastMessage("Ett fel uppstod vid uppdatering av synlighet.");
    } finally {
      setVisBusy(false);
    }
  };

  // Add employee (popup)
  const addEmployeeToProject = async (employeeId) => {
    if (!employeeId || employeeId === 'all') return;
    if (selectedProject.employees.includes(employeeId)) {
      setToastType("error");
      setToastMessage("Användare är redan tillagd i projektet!");
      return;
    }

    const data = await add_employee_to_project({
      user,
      company,
      project: selectedProject,
      employeeId
    });

    if (data.status === "success") {
      setProjects(prev =>
        prev.map(p => p._id === selectedProject._id ? data.project : p)
      );
      setAllProjects(prev => {
        const updated = prev.map(p =>
          p._id === selectedProject._id ? data.project : p
        );
        updateUnassignedWarnings(updated);
        return updated;
      });
      setToastType("success");
      setToastMessage("Användare tillagd i projektet!");
      setSelectedProject(data.project);

      setEmployeeForProjectInput("");
      setSelectedEmployeeForProject(null);
    } else {
      setToastType("error");
      setToastMessage(data.errorText);
    }
  };

  // Remove employee (popup)
  const removeEmployeeFromProject = async (employeeId) => {
    if (!employeeId || employeeId === 'all') return;
    if (!selectedProject.employees.includes(employeeId)) {
      setToastType("error");
      setToastMessage("Användare finns inte i projektet!");
      return;
    }

    const data = await remove_employee_from_project({
      user,
      company,
      project: selectedProject,
      employeeId
    });

    if (data.status === "success") {
      setProjects(prev =>
        prev.map(p => p._id === selectedProject._id ? data.project : p)
      );
      setAllProjects(prev => {
        const updated = prev.map(p =>
          p._id === selectedProject._id ? data.project : p
        );
        updateUnassignedWarnings(updated);
        return updated;
      });
      setToastType("success");
      setToastMessage("Användare borttagen från projektet!");
      setSelectedProject(data.project);

      setEmployeeForProjectInput("");
      setSelectedEmployeeForProject(null);
    } else {
      setToastType("error");
      setToastMessage(data.errorText);
    }
  };

  // ===== Clean, Simple Popup UI =====
  const projectPopup = () => (
    <div
      className="project-popup-overlay"
      onClick={() => setProjectPopupVisible(false)}
    >
      <div className="project-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <h2 className="project-name">
            {selectedProject?.name || "Projekt"}
          </h2>
          <p style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
            {selectedProject?.employees?.length || 0} anställda i projektet
          </p>
        </div>

        <div
          style={{
            background: "#f8f9fa",
            border: "1px solid #e5e7eb",
            color: "#111827",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.4,
            margin: "0 auto 14px auto",
            maxWidth: 720,
            textAlign: "center"
          }}
        >
          Lägg till/ta bort anställda och ställ in om personen ska <b>se alla</b> pass i projektet
          eller <b>bara sina egna</b>. Klicka på knappen på varje rad för att växla.
        </div>

        {/* Assign / Remove */}
        <div className="assign-user-area">
          <div style={{ position: "relative", display: "inline-block", width: 260 }}>
            <input
              list="employeeList"
              value={employeeForProjectInput}
              onChange={(e) => {
                const name = e.target.value;
                setEmployeeForProjectInput(name);
                const emp = allEmployees.find((emp) => emp.name === name);
                setSelectedEmployeeForProject(emp ? emp._id : null);
              }}
              className="employee-select"
              placeholder="Sök eller välj anställd…"
              style={{
                width: "100%",
                paddingRight: "28px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
              required
            />
            {employeeForProjectInput && (
              <button
                onClick={() => {
                  setEmployeeForProjectInput("");
                  setSelectedEmployeeForProject(null);
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "53%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  color: "#9ca3af",
                  fontSize: 18,
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: 0,
                }}
                title="Rensa"
              >
                ×
              </button>
            )}
            <datalist id="employeeList">
              {allEmployees.map((emp) => (
                <option key={emp._id} value={emp.name} />
              ))}
            </datalist>
          </div>

          {canEditProjects && (
            <>
            <button
              className="add-user-button"
              onClick={() =>
                selectedEmployeeForProject &&
                addEmployeeToProject(selectedEmployeeForProject)
              }
            >
              Lägg till
            </button>

            <button
              className="remove-user-button"
              onClick={() =>
                selectedEmployeeForProject &&
                removeEmployeeFromProject(selectedEmployeeForProject)
              }
            >
              Ta bort
            </button>
            </>
          )}
        </div>

        {/* Employees list */}
        <div className="assigned-employees">
          <h3 className="section-header">Anställda i projektet</h3>

          <div className="employee-list">
            {selectedProject?.employees?.length > 0 ? (
              (() => {
                const empIds = selectedProject.employees || [];
                const orderedEmpIds =
                  selectedEmployeeForProject && empIds.includes(selectedEmployeeForProject)
                    ? [selectedEmployeeForProject, ...empIds.filter((id) => id !== selectedEmployeeForProject)]
                    : empIds;

                return orderedEmpIds.map((empId, i) => {
                  const emp = allEmployees.find((e) => e._id === empId);
                  console.log("EMP DEBUG (PROJECT POPUP):", {
                    empId,
                    foundName: emp?.name,
                  });
                  const isSelected = selectedEmployeeForProject === empId;
                  const seesAll = canSeeAll(selectedProject, empId);

                  return (
                    <div
                      key={empId || i}
                      className="employee-row"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedEmployeeForProject(null);
                          setEmployeeForProjectInput("");
                        } else {
                          setSelectedEmployeeForProject(empId);
                          setEmployeeForProjectInput(emp?.name || "");
                        }
                      }}
                      style={{
                        borderLeft: "4px solid #5DADE2",
                        outline: isSelected ? "2px solid #5DADE2" : "none",
                        background: isSelected ? "#f0f7ff" : "#ffffff",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                        <p className="employee-name" style={{ margin: 0, fontSize: 14, fontWeight: isSelected ? 600 : 400 }}>
                          {emp?.name || ""}
                        </p>
                        <span
                          style={{
                            fontSize: 11,
                            padding: "4px 8px",
                            borderRadius: 4,
                            background: seesAll ? "#e8f4ff" : "#f3f4f6",
                            border: seesAll ? "1px solid #5DADE2" : "1px solid #d1d5db",
                            color: seesAll ? "#1e40af" : "#374151",
                            fontWeight: 500,
                          }}
                        >
                          {seesAll ? "Ser alla pass" : "Ser bara egna"}
                        </span>
                      </div>

                      {canEditProjects && (
                        <button
                          disabled={visBusy || !selectedProject?._id}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!selectedProject?._id) return;
                            setMemberVisibility({
                              projectId: selectedProject._id,
                              targetUserId: empId,
                              canSeeAllShifts: !seesAll
                            });
                          }}
                          title={seesAll ? "Ändra till bara egna pass" : "Ändra till alla pass"}
                          style={{
                            padding: "6px 12px",
                            fontSize: 12,
                            borderRadius: 4,
                            border: "1px solid #5DADE2",
                            background: seesAll ? "#ffffff" : "#5DADE2",
                            color: seesAll ? "#5DADE2" : "#ffffff",
                            cursor: visBusy ? "not-allowed" : "pointer",
                            fontWeight: 500,
                            minWidth: 140,
                            textAlign: "center"
                          }}
                        >
                          {seesAll ? "Byt till bara egna" : "Byt till alla pass"}
                        </button>
                      )}

                    </div>
                  );
                });
              })()
            ) : (
              <p className="no-employees">Inga anställda tillagda i projektet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const exportProjectsToExcel = () => {
    const rows = allProjects.map((project) => ({
      Projektnamn: project.name || "",

      Kund:
        allCustomers.find((c) =>
          (project.customer &&
          project.customer._id)
            ? c._id ===
              project.customer._id
            : c._id ===
              project.customer
        )?.name || "",

      Adress:
        project.location || "",

      Instruktioner:
        project.instructions || "",
    }));

    const worksheet =
      XLSX.utils.json_to_sheet(rows);

    const longestProjectName = Math.max(
      "Projektnamn".length,
      ...rows.map(
        (r) => (r.Projektnamn || "").length
      )
    );

    worksheet["!cols"] = [
      {
        wch:
          Math.max(
            longestProjectName + 8,
            35
          ),
      }, // Projektnamn

      {
        wch: 35,
      }, // Kund

      {
        wch: 35,
      }, // Adress

      {
        wch: 60,
      }, // Instruktioner
    ];

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Projekt"
    );

    XLSX.writeFile(
      workbook,
      `Projekt-${company.name}.xlsx`
    );
  };

  const yearOptions = [];
  for (let y = initYear - 2; y <= initYear + 2; y++) yearOptions.push(y);

  return (
    <div className="projects-page">
      <Nav />
      <div className="page-header">
        <h1>Projekt</h1>

        {canEditProjects && (
          <>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 8,
                position: "relative",
              }}
            >
              {/* ADD PROJECT */}
              <button
                className="add-button"
                onClick={togglePopup}
                style={{
                  height: 40,
                  padding: "0 14px",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <FaPlus style={{ fontSize: 11 }} />
                Lägg till projekt
              </button>

              {/* MORE BUTTON */}
              <button
                onClick={() =>
                  setShowImportMenu(
                    (prev) => !prev
                  )
                }
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#374151",
                  fontSize: 14,
                }}
              >
                <FaEllipsisH />
              </button>

              {/* DROPDOWN */}
              {showImportMenu && (
                <div
                  style={{
                    position: "absolute",
                    top: 46,
                    right: 0,
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    boxShadow:
                      "0 10px 30px rgba(0,0,0,0.12)",
                    padding: 6,
                    minWidth: 220,
                    zIndex: 99999,
                  }}
                >
                  <div
                    onClick={() => {
                      setShowImportMenu(false);
                      setShowImportProjectsPopup(
                        true
                      );
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 12px",
                      borderRadius: 8,
                      fontSize: 13,
                      cursor: "pointer",
                      color: "#111827",
                      userSelect: "none",
                    }}
                  >
                    <SiGooglesheets
                      style={{ fontSize: 15 }}
                    />
                    Importera projekt
                  </div>
                  <div
                  onClick={() => {
                    setShowImportMenu(false);

                    exportProjectsToExcel();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 8,
                    fontSize: 13,
                    cursor: "pointer",
                    color: "#111827",
                    userSelect: "none",
                  }}
                >
                  <SiGooglesheets
                    style={{ fontSize: 15 }}
                  />
                  Exportera Excel
                </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <div>

        {/* --- Konfliktruta + selects --- */}
        <div
          className={`project-alert-feed ${isFetching ? "loading" : ""}`}
          style={{ position: "relative" }}
        >
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
            flexWrap: "wrap"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
                <FaCalendarAlt style={{ opacity: 0.8 }} />
                Period
              </span>

              <select
                aria-label="Välj år"
                value={inputYear}
                onChange={onChangeYear}
                style={{
                  height: 32,
                  padding: "4px 8px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  fontSize: 13,
                  color: "#374151",
                  background: "#ffffff",
                  outline: "none"
                }}
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              <select
                aria-label="Välj månad"
                value={inputMonth}
                onChange={onChangeMonth}
                style={{
                  height: 32,
                  padding: "4px 8px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  fontSize: 13,
                  color: "#374151",
                  background: "#ffffff",
                  outline: "none"
                }}
              >
                {monthNames.map((name, idx) => (
                  <option key={name} value={idx + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginLeft: "auto" }}>
              {canEditProjects && (
                <button
                  onClick={handleQuickAddAllEmployeesToProjects}
                  disabled={unassignedWarnings.length === 0 || isBulkAdding}
                  style={{
                    height: 32,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "0 10px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    background: "#fff",
                    color: "#374151",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: (unassignedWarnings.length === 0 || isBulkAdding) ? "not-allowed" : "pointer",
                    opacity: (unassignedWarnings.length === 0 || isBulkAdding) ? 0.6 : 1
                  }}
                  title={unassignedWarnings.length === 0 ? "Inga konflikter att åtgärda" : "Lägg till alla berörda anställda i sina projekt"}
                >
                  <FaPlus style={{ fontSize: 12, opacity: 0.85 }} />
                  {isBulkAdding ? "Lägger till…" : "Lägg till alla"}
                  <span
                    style={{
                      display: "inline-flex",
                      minWidth: 18,
                      height: 18,
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 9,
                      fontSize: 11,
                      fontWeight: 700,
                      background: "#f3f4f6",
                      color: "#374151",
                      padding: "0 6px"
                    }}
                  >
                    {unassignedWarnings.length}
                  </span>
                </button>
              )}

            </div>
          </div>

          <div style={{ marginBottom: 8, fontSize: 13, color: "#6b7280", fontWeight: 500 }}>
            {`Visar konflikter för schemalagda pass inom ${monthLabel}`}
          </div>

          {unassignedWarnings.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af" }}>Inga konflikter just nu</p>
          ) : (
            unassignedWarnings.map((item, idx) => (
              <div key={idx} className="alert-message">
                <span className="alert-text">
                  <strong>{item.employeeName}</strong> är schemalagd för{" "}
                  <strong>{item.missingShiftCount}</strong> pass i{" "}
                  <strong>{item.projectName}</strong>, men är inte tillagd i projektet.
                </span>

                {canEditProjects && (
                  <button
                    className="add-to-project-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAddToProject(item.employeeId, item.projectId);
                    }}
                  >
                    Lägg till
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        <input
          type="text"
          className="search-input"
          placeholder="sök projekt"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {isPopupOpen && (
        <div className="project-popup-overlay active" onClick={() => setIsPopupOpen(false)}>
          <div className="popup" onClick={e => e.stopPropagation()}>
            <h4>{selectedProject ? 'Redigera projekt' : 'Lägg till nytt Projekt'}</h4>
            <form onSubmit={handleSubmit} className="project-form">
              <input
                type="text"
                value={newProjectName}
                onChange={e => setNewProjectName(e.target.value)}
                placeholder="Projekt Namn"
                required
              />
              <input
                type="text"
                value={newProjectLocation}
                onChange={e => setNewProjectLocation(e.target.value)}
                placeholder="Område/stad (valfritt)"
              />
              <textarea
                value={newProjectInstructions}
                onChange={e => setNewProjectInstructions(e.target.value)}
                placeholder="Instruktioner (valfritt)"
              />

              <label htmlFor="customer-input">Kund:</label>
              <input
                id="customer-input"
                list="customer-list"
                value={newProjectCustomer?.name || ''}
                onChange={e => {
                  const name = e.target.value;
                  const existing = allCustomers.find(c => c.name === name);
                  if (existing) {
                    setNewProjectCustomer({ _id: existing._id, name: existing.name });
                  } else {
                    setNewProjectCustomer({ _id: null, name });
                  }
                }}
                placeholder="Välj eller skriv kund..."
              />
              <datalist id="customer-list">
                <option value="">Ingen kund</option>
                {allCustomers.map(cust => (
                  <option key={cust._id} value={cust.name} />
                ))}
              </datalist>

              <button type="submit">
                {selectedProject ? 'Spara ändringar' : 'Skapa Project'}
              </button>
              <button
                type="button"
                className="close-button"
                onClick={() => setIsPopupOpen(false)}
              >
                Stäng
              </button>
            </form>
          </div>
        </div>
      )}

      {projectPopupVisible && projectPopup()}

      <div className="projects-list">
        <div className="column-headers">
          <p className="column-header name-header">Namn</p>
          <p className="column-header location-header">Område/Stad</p>
          <p className="column-header customer-header">Kund</p>
          <div className="column-action-placeholder" />
        </div>

        {filteredProjects.map(project => (
          <div
            className="project-row"
            key={project._id}
            onClick={() => handleShowProjectPopupClicked(project)}
          >
            <p className="project-name">{project.name}</p>
            <p className="project-location">{project.location}</p>
            <p className="project-customer">
              {allCustomers.find(c =>
                (project.customer && project.customer._id)
                  ? c._id === project.customer._id
                  : c._id === project.customer
              )?.name || ''}
            </p>

            <div
              className={`project-actions ${activeMenu === project._id ? 'active' : ''}`}
              ref={activeMenu === project._id ? menuRef : null}
            >
              {canEditProjects && (
                <button
                  className="menu-button"
                  onClick={e => toggleMenu(project._id, e)}
                >
                  ⋮
                </button>
              )}
              {activeMenu === project._id && (
                <div className="action-menu">
                  <button onClick={e => { e.stopPropagation(); handleEdit(project); }}>
                    Redigera
                  </button>
                  <button onClick={e => { e.stopPropagation(); handleDelete(project); }}>
                    Radera
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <ImportProjectsPopup
        visible={showImportProjectsPopup}
        onClose={() =>
          setShowImportProjectsPopup(false)
        }
        onImport={async ({
          parsedRows,
          columnMapping,
        }) => {
        const response =
          await axios.post(
            `${BACKEND_URL}/import-projects`,
            {
              currentUserId:
                user._id,
              companyId:
                company._id,
              parsedRows,
              columnMapping,
            }
          );

        if (
          response.data.status ===
          "success"
        ) {
          setProjects(
            response.data.projects
          );

          setAllProjects(
            response.data.projects
          );

          setCustomers(
            response.data.customers
          );  

          setAllCustomers(
            response.data.customers
          );

          setToastType("success");

          setToastMessage(
            `${response.data.importedCount} projekt importerade`
          );

          setShowImportProjectsPopup(
            false
          );
        }

          setShowImportProjectsPopup(false);
        }}
      />

      <Toast
        message={toastMessage}
        type={toastType}
        onClose={() => setToastMessage("")}
      />
    </div>
  );
};

export default Projects;